import { Grid, GridColumn as Column } from '@progress/kendo-react-grid'
import { HeaderComponent } from 'components/header/component'
import { ComboBox } from '@progress/kendo-react-dropdowns'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import React, { Component } from 'react'
import { Card, CardBody, Badge, Label, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Input } from 'reactstrap'
import * as Constants from './constants'
import {
  EmailConfigurationListState,
  Newsletter
} from './types'
import './_styles.scss'

export class EmailConfigurationsComponent extends Component<Record<string, never>, EmailConfigurationListState> {
  public static contextType = RasaContext
  private newsletterSearchTimer: any

  constructor(props: Record<string, never>) {
    super(props)
    this.state = {
      configurations: [],
      loading: false,
      saving: false,
      error: undefined,
      data: [],
      newsletters: [],
      filteredNewsletters: [],
      selectedNewsletter: undefined,
      loadingNewsletters: false,
      showModal: false,
      editingConfiguration: undefined,
      confirmDelete: null,
      confirmSetPrimary: null,
      formData: {
        display_name: '',
        sender: 'sendgrid',
        is_primary: false,
        config: {
          api_key: '',
          from_address: '',
          from_name: ''
        }
      },
      availableConfigurations: [],
      selectedConfigurationToAdd: undefined,
      loadingAvailableConfigurations: false
    }
  }

  public componentDidMount() {
    this.loadAllEmailConfigurations()
    this.loadNewsletters()
  }

  public render() {
    return (
      <div className="email-configurations-wrapper">
        <HeaderComponent title="Email Configuration Management" subTitle="View all customer email configurations" />

        <Card className="mb-4 email-configurations-card">
          <CardBody className="p-4">
            <div className="row align-items-center">
              <div className="col-md-3">
                <Label className="mb-0">Filter by Newsletter:</Label>
              </div>
              <div className="col-md-9">
                <div style={{ maxWidth: '500px' }}>
                  {this.renderNewsletterSearch()}
                  <small className="text-muted d-block mt-1">
                    Leave empty to show all configurations
                    {this.state.loadingNewsletters && ' • Loading newsletters...'}
                  </small>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="email-configurations-card">
          <CardBody className="p-4">
            {this.state.selectedNewsletter && (
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                  <strong>Configurations for: {this.state.selectedNewsletter.community_name}</strong>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={this.handleAddConfiguration}
                  disabled={this.state.loading}
                >
                  + Add Configuration
                </button>
              </div>
            )}
            {this.renderEmailConfigurationsGrid()}
          </CardBody>
        </Card>

        {this.renderAddConfigurationModal()}

      </div>
    )
  }


  private renderEmailConfigurationsGrid = () => {
    if (this.state.loading) {
      return <Loading size="32" />
    }

    if (this.state.error) {
      return (
        <div className="alert alert-danger">
          {this.state.error}
        </div>
      )
    }

    // Removed newsletter dependency - show all configurations by default

    if (this.state.configurations.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          No email configurations found for the selected newsletter.
        </div>
      )
    }

    return (
      <Grid
        data={this.state.data}
        sortable={true}
        pageable={false}>
        <Column cell={this.ProviderCell} title="Provider" width={120} />
        <Column field="display_name" title="Display Name" />
        <Column cell={this.DomainsCell} title="Domains" width={150} />
        <Column cell={this.AccountCell} title="Account" width={150} />
        <Column cell={this.ValidStatusCell} title="Valid" width={80} />
        {this.state.selectedNewsletter && (
          <Column cell={this.PrimaryCheckCell} title="Primary" width={80} />
        )}
        <Column cell={this.CreatedCell} title="Created" width={120} />
        <Column cell={this.UpdatedCell} title="Updated" width={120} />
      </Grid>
    )
  }




  private loadAllEmailConfigurations = async (selectedNewsletter?: Newsletter) => {
    this.setState({ loading: true, error: undefined })

    try {
      const dataset = new Dataset()

      let params = []
      let datasetId = Constants.DATASET_IDS.EMAIL_CONFIGURATIONS

      if (selectedNewsletter && selectedNewsletter.account_id) {
        // Use new accountEmailConfigurations dataset with account_id parameter
        datasetId = Constants.DATASET_IDS.ACCOUNT_EMAIL_CONFIGURATIONS
        params = [{ param: 'accountId', value: selectedNewsletter.community_account_id }]
      }
      // If no newsletter selected, load all configurations using original dataset

      const results = await dataset.loadGlobalDataset(datasetId, params)

      this.setState({
        configurations: results[0] || [],
        data: results[0] || [],
        loading: false
      })
    } catch (error) {
      this.setState({
        loading: false,
        error: Constants.MESSAGES.ERROR_LOADING_CONFIGURATIONS
      })
    }
  }

  private loadNewsletters = async (filterVal?: string) => {
    this.setState({ loadingNewsletters: true })

    try {
      const dataset = new Dataset()
      // Load newsletters for dropdown - using userNewsletters dataset with server-side filtering
      const params = []
      if (filterVal) {
        params.push({ param: 'filterVal', value: filterVal })
        params.push({ param: 'startPage', value: 0 })
        params.push({ param: 'pageSize', value: 25 })
      }

      const results = await dataset.loadGlobalDataset('userNewsletters', params)

      this.setState({
        newsletters: results[0] || [],
        filteredNewsletters: results[0] || [],
        loadingNewsletters: false
      })
    } catch (error) {
      this.setState({
        loadingNewsletters: false,
        error: 'Failed to load newsletters'
      })
    }
  }

  private renderNewsletterSearch = () => {
    return (
      <ComboBox
        placeholder="Search by name, identifier, or ID..."
        data={this.state.filteredNewsletters}
        textField="community_name"
        filterable={true}
        value={this.state.selectedNewsletter}
        onChange={this.handleNewsletterChange}
        disabled={this.state.loadingNewsletters}
        onFilterChange={this.handleNewsletterFilterChange}
        itemRender={this.renderNewsletterItem}
        style={{ width: '100%' }}
      />
    )
  }

  private renderNewsletterItem = (li: any, itemProps: any) => {
    const { dataItem } = itemProps
    return React.cloneElement(li, li.props, (
      <div>
        <div><strong>{dataItem.community_name}</strong></div>
        <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
          ID: {dataItem.community_id} | Identifier: {dataItem.community_identifier}
        </div>
      </div>
    ))
  }

  private handleNewsletterFilterChange = (event: any) => {
    const filter = event.filter
    const searchTerm = filter && filter.value ? filter.value : ''

    // Clear existing timer
    if (this.newsletterSearchTimer) {
      clearTimeout(this.newsletterSearchTimer)
    }

    // Debounce the search with 500ms delay
    this.newsletterSearchTimer = setTimeout(() => {
      this.loadNewsletters(searchTerm)
    }, 500)
  }

  private handleNewsletterChange = (event: any) => {
    const selectedNewsletter: Newsletter = event.target.value
    this.setState({
      selectedNewsletter
    }, () => {
      this.loadAllEmailConfigurations(selectedNewsletter)
    })
  }

  private handleAddConfiguration = async () => {
    this.setState({
      showModal: true,
      editingConfiguration: undefined,
      loadingAvailableConfigurations: true
    })

    await this.loadAvailableConfigurations()
  }

  private loadAvailableConfigurations = async () => {
    if (!this.state.selectedNewsletter) return

    try {
      const dataset = new Dataset()

      // Load all email configurations
      const allConfigsResults = await dataset.loadGlobalDataset(Constants.DATASET_IDS.EMAIL_CONFIGURATIONS, [])
      const allConfigs = allConfigsResults[0] || []

      // Get currently assigned configuration IDs for this account
      const currentConfigIds = this.state.configurations.map(config => config.email_configuration_id)

      // Filter out configurations that are already assigned to this account
      const availableConfigs = allConfigs.filter(config =>
        !currentConfigIds.includes(config.id)
      )

      this.setState({
        availableConfigurations: availableConfigs,
        loadingAvailableConfigurations: false
      })
    } catch (error) {
      this.setState({
        loadingAvailableConfigurations: false,
        error: 'Failed to load available configurations'
      })
    }
  }

  private handleTogglePrimary = async (config: any) => {
    if (!this.state.selectedNewsletter) return

    this.setState({ saving: true })

    try {
      // Use the entity operations to update the configuration with community ID
      const entity = await this.context.entityMetadata.getEntityObject(
        Constants.DATASET_IDS.ACCOUNT_EMAIL_CONFIG,
        this.state.selectedNewsletter.community_id.toString(),
        config.id
      )

      // Update is_primary to true
      entity.data.is_primary = true

      await entity.save()

      // Update UI state locally instead of reloading from server
      this.updatePrimaryStatusLocally(config.id)

    } catch (error) {
      this.setState({
        error: 'Failed to update primary status'
      })
    } finally {
      this.setState({ saving: false })
    }
  }

  private updatePrimaryStatusLocally = (newPrimaryConfigId: number) => {
    const updatedConfigurations = this.state.configurations.map(config => ({
      ...config,
      is_primary: config.id === newPrimaryConfigId
    }))

    const updatedData = this.state.data.map(config => ({
      ...config,
      is_primary: config.id === newPrimaryConfigId
    }))

    this.setState({
      configurations: updatedConfigurations,
      data: updatedData
    })
  }

  private renderAddConfigurationModal = () => {
    return (
      <Modal isOpen={this.state.showModal} toggle={this.handleCloseModal}>
        <ModalHeader toggle={this.handleCloseModal}>
          Add Email Configuration to {this.state.selectedNewsletter && this.state.selectedNewsletter.community_name}
        </ModalHeader>
        <ModalBody>
          {this.state.loadingAvailableConfigurations ? (
            <div className="text-center">
              <Loading size="32" />
              <p className="mt-2">Loading available configurations...</p>
            </div>
          ) : this.state.availableConfigurations.length === 0 ? (
            <div className="text-center text-muted">
              <p>No additional email configurations are available to add.</p>
              <p>All existing configurations are already assigned to this newsletter.</p>
            </div>
          ) : (
            <Form>
              <FormGroup>
                <Label for="configurationSelect">Select Configuration to Add:</Label>
                <Input
                  type="select"
                  name="configurationSelect"
                  id="configurationSelect"
                  value={this.state.selectedConfigurationToAdd && this.state.selectedConfigurationToAdd.id || ''}
                  onChange={this.handleConfigurationSelection}
                >
                  <option value="">-- Select a configuration --</option>
                  {this.state.availableConfigurations.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.display_name || config.name} ({config.sender.toUpperCase()})
                    </option>
                  ))}
                </Input>
              </FormGroup>
              {this.state.selectedConfigurationToAdd && (
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <h6>Configuration Details:</h6>
                  <p><strong>Name:</strong> {this.state.selectedConfigurationToAdd.display_name || this.state.selectedConfigurationToAdd.name}</p>
                  <p><strong>Provider:</strong> {this.state.selectedConfigurationToAdd.sender.toUpperCase()}</p>
                  <p><strong>Status:</strong> {this.state.selectedConfigurationToAdd.status}</p>
                </div>
              )}
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={this.handleAddSelectedConfiguration}
            disabled={this.state.saving || !this.state.selectedConfigurationToAdd || this.state.loadingAvailableConfigurations}
          >
            {this.state.saving ? 'Adding...' : 'Add Configuration'}
          </Button>
          <Button color="secondary" onClick={this.handleCloseModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private handleCloseModal = () => {
    this.setState({
      showModal: false,
      selectedConfigurationToAdd: undefined,
      availableConfigurations: [],
      formData: {
        display_name: '',
        sender: 'sendgrid',
        is_primary: false,
        config: {
          api_key: '',
          from_address: '',
          from_name: ''
        }
      }
    })
  }

  private handleConfigurationSelection = (event: any) => {
    const selectedId = parseInt(event.target.value, 10)
    const selectedConfig = this.state.availableConfigurations.find(config => config.id === selectedId)
    this.setState({
      selectedConfigurationToAdd: selectedConfig
    })
  }

  private handleAddSelectedConfiguration = async () => {
    if (!this.state.selectedNewsletter || !this.state.selectedConfigurationToAdd) return

    this.setState({ saving: true })

    try {
      // Create new entity object with community ID for new record
      const entity = await this.context.entityMetadata.getEntityObject(
        Constants.DATASET_IDS.ACCOUNT_EMAIL_CONFIG
      )
      entity.setRecordId(this.state.selectedNewsletter.community_identifier)

      // Set the correct parameters that the stored procedure expects
      entity.data.account_id = this.state.selectedNewsletter.community_account_id
      entity.data.email_configuration_id = this.state.selectedConfigurationToAdd.id // Send specific configuration ID
      entity.data.is_primary = false // New configurations are not primary by default
      entity.data.display_name = this.state.selectedConfigurationToAdd.display_name || this.state.selectedConfigurationToAdd.name || `${this.state.selectedConfigurationToAdd.sender} Configuration`

      await entity.save()

      // Close modal and reload configurations
      this.handleCloseModal()
      this.loadAllEmailConfigurations(this.state.selectedNewsletter)

    } catch (error) {
      this.setState({
        error: 'Failed to add configuration'
      })
    } finally {
      this.setState({ saving: false })
    }
  }

  private parseConfigAccount = (config: any): string => {
    try {
      // If config is already an object, use it directly
      let configObj = config

      // If config is a string, try to parse it as JSON
      if (typeof config === 'string') {
        configObj = JSON.parse(config)
      }

      // Return the account attribute if it exists
      if (configObj && configObj.account) {
        return configObj.account
      }

      return '-'
    } catch (error) {
      // If parsing fails, return error indicator
      return 'Parse Error'
    }
  }

  private ProviderCell = (props: any) => {
    return (
      <td>
        <Badge color="info">{props.dataItem.sender.toUpperCase()}</Badge>
      </td>
    )
  }

  private AccountCell = (props: any) => {
    return (
      <td>
        {this.parseConfigAccount(props.dataItem.config)}
      </td>
    )
  }

  private CreatedCell = (props: any) => {
    return (
      <td>
        {new Date(props.dataItem.created).toLocaleDateString()}
      </td>
    )
  }

  private UpdatedCell = (props: any) => {
    return (
      <td>
        {new Date(props.dataItem.updated).toLocaleDateString()}
      </td>
    )
  }




  private PrimaryCheckCell = (props: any) => {
    const config = props.dataItem
    const isPrimary = config.is_primary
    const isValid = config.is_active
    
    return (
      <td className="text-center">
        {isPrimary ? (
          <span 
            className="text-success" 
            style={{ fontSize: '1.2em', cursor: 'default' }}
            title="This is the primary configuration"
          >
            ✓
          </span>
        ) : isValid ? (
          <button
            className="btn btn-link p-0 text-muted"
            onClick={() => this.handleTogglePrimary(config)}
            disabled={this.state.saving}
            title="Click to set as primary"
            style={{ 
              fontSize: '1.2em', 
              border: 'none', 
              background: 'none',
              textDecoration: 'none'
            }}
          >
            ○
          </button>
        ) : (
          <span 
            className="text-muted" 
            style={{ fontSize: '1.2em' }}
            title="Cannot set as primary - configuration is invalid"
          >
            -
          </span>
        )}
      </td>
    )
  }

  private ValidStatusCell = (props: any) => {
    const isValid = props.dataItem.is_active
    return (
      <td className="text-center">
        {isValid ? (
          <span 
            className="text-success" 
            style={{ fontSize: '1.2em' }}
            title="Configuration is valid"
          >
            ✓
          </span>
        ) : (
          <span 
            className="text-danger" 
            style={{ fontSize: '1.2em' }}
            title="Configuration is invalid"
          >
            ✗
          </span>
        )}
      </td>
    )
  }

  private DomainsCell = (props: any) => {
    const domains = props.dataItem.domains
    return (
      <td>
        {domains ? (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '140px' }} title={domains}>
            {domains}
          </span>
        ) : (
          <span className="text-muted">-</span>
        )}
      </td>
    )
  }














}